import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { can, canObserve, canAdminister, PERMISSIONS } from '../lib/permissions';

/* ── Icons ────────────────────────────────────────────────────────────────── */
const Icon = {
  home: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2 7.5L9 2l7 5.5V16H12v-4H6v4H2V7.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    </svg>
  ),
  observe: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.3"/>
      <circle cx="9" cy="9" r="2.5" fill="currentColor"/>
    </svg>
  ),
  list: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3 5h12M3 9h9M3 13h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  chart: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="9" width="3" height="7" rx="0.5" stroke="currentColor" strokeWidth="1.3"/>
      <rect x="7.5" y="5" width="3" height="11" rx="0.5" stroke="currentColor" strokeWidth="1.3"/>
      <rect x="13" y="2" width="3" height="14" rx="0.5" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  ),
  download: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 2v9m0 0l3.2-3.2M9 11L5.8 7.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 13v2h12v-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  cog: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="6" r="2.6" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M3.8 15c0-2.6 2.3-4.2 5.2-4.2s5.2 1.6 5.2 4.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
};

/**
 * The bottom navigation.
 *
 * What it shows follows what the account can do. An observer gets the capture
 * flow and their own history; an administrator gets the data and management
 * screens instead, because they never observe.
 */
export default function TabBar({ current }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const items = [{ k: 'home', l: 'Home', icon: Icon.home, to: '/' }];

  if (canObserve(user)) {
    items.push({ k: 'observe', l: 'Observe', icon: Icon.observe, to: '/observe/context' });
    if (can(user, 'view_own_history')) {
      items.push({ k: 'history', l: 'History', icon: Icon.list, to: '/history' });
    }
    if (can(user, PERMISSIONS.SCHOOL_DASHBOARD) || can(user, 'patch_dashboard')) {
      items.push({ k: 'dashboard', l: 'Dashboard', icon: Icon.chart, to: '/dashboard' });
    }
  } else {
    // Administration-side navigation.
    if (can(user, 'all_school_observations')) {
      items.push({ k: 'observations', l: 'Observations', icon: Icon.list, to: '/observations' });
    }
    if (can(user, PERMISSIONS.SCHOOL_DASHBOARD) || can(user, 'district_dashboard')) {
      items.push({ k: 'dashboards', l: 'Dashboards', icon: Icon.chart, to: '/dashboards' });
    }
    if (can(user, 'growth_data_export') || can(user, 'research_data_export')) {
      items.push({ k: 'exports', l: 'Exports', icon: Icon.download, to: '/exports' });
    }
    if (canAdminister(user)) {
      items.push({ k: 'admin', l: 'Manage', icon: Icon.cog, to: '/admin' });
    }
  }

  return (
    <div className="tabbar">
      {items.map(item => (
        <button key={item.k}
                className={`tab${current === item.k ? ' on' : ''}`}
                onClick={() => current !== item.k && navigate(item.to)}>
          {item.icon}
          {item.l}
        </button>
      ))}
    </div>
  );
}
